// Shared club-identity copy + colour helpers, used by the hub's ClubModal.
// NOTE: ExploreTeams.js (the live public carousel) still keeps its own copy of
// IDENTITY_LINES/helpers; this mirrors it. Consolidate into the DB later
// (see HANDOFF backlog) rather than editing the live carousel.

export const TIER_NAMES = {
  1: 'Premier League', 2: 'Championship', 3: 'League One', 4: 'League Two',
};

export const IDENTITY_LINES = {
  'Hartwell City FC': 'Stylish possession football, powered by ambitious ownership.',
  'Caldermoor United': 'A fierce high press under proud local ownership.',
  'Ferndale Athletic': 'Community-owned and lethal on the counter.',
  'Stonebridge Rovers': 'Old-fashioned direct football under a local owner.',
  'Ashbourne FC': 'A patient passing side spending big under new investment.',
  'Millbrook Town': 'Fan-owned and relentless, pressing from the front.',
  'Wrenfield Borough': 'Stubborn counter-attackers under modest local ownership.',
  'Dunsley FC': 'Direct and still dangerous, drifting under an absent owner.',
  'Brackley Vale FC': 'Community-owned and devoted to attractive, patient football.',
  'Thornwick Town': 'Compact counter-punchers backed by a local businessman.',
  'Crestham United': 'An aggressive press driven by ambitious, restless owners.',
  'Holmbury City': 'Direct and fearless, newly promoted under local ownership.',
  'Greenvale United': 'Community-run and comfortable in possession.',
  'Ashworth FC': 'Direct football and fierce local pride under a hometown owner.',
  'Cressford Athletic': 'High-tempo pressing under a driven local owner.',
  'Pelham Town': 'Community-owned counter-attackers biding their time.',
  'Northgate City': 'A fallen giant passing its way back under new investment.',
  'Ravensmoor FC': 'Physical, direct and hard to beat under local ownership.',
  'Holtby Wanderers': 'Fan-owned and ever-adapting on the counter.',
  'Sedgewick Town': 'Direct football and market-town grit under a local owner.',
  'Elmwick Borough': 'Tidy in possession and ambitious under local ownership.',
  'Dunford City': 'A pressing side building fast on fresh investment.',
  'Blackfen Rovers': 'Community-owned, direct and famously hard to beat.',
  'Marefield United': 'Counter-attacking newcomers backed by a local owner.',
  'Millhaven Borough': 'Direct football and terrace passion under local ownership.',
  'Thornfield FC': 'Community-owned and quietly building a passing side.',
  'Copsley Town': 'Direct and thrifty, run the right way by a local owner.',
  'Harwick United': 'A relentless press under motivated local ownership.',
  'Fernsby Athletic': 'Fan-owned counter-attackers running on team spirit.',
  'Woldgate Town': 'Community-owned and proudly direct, non-league at heart.',
  'Moorside FC': 'Rugged, direct football under a local owner.',
  'Ridgeley Rovers': 'Community-owned giant-killers on the counter.',
  'Langport City': 'A passing side with big plans under new investment.',
  'Beckford Town': 'Direct and well-drilled under a shrewd local owner.',
  'Dalesbury FC': 'Fan-owned and pressing like their lives depend on it.',
  'Fenwick Athletic': 'Community-owned counter-attackers, small but fearless.',
  'Hareford Town': 'Direct football, adrift under an absent owner.',
  'Penwick FC': 'Volunteer-run and direct, determined to climb.',
  'Brockwell United': 'Counter-attacking hopefuls backed by a local benefactor.',
  'Sladden Town': 'Direct football under a distant, absent owner.',
  'Grantley FC': 'Community-owned and pressing with real energy.',
  'Wyberton Athletic': 'Fan-owned counter-attackers, grassroots made good.',
  'Lowmoor FC': 'Direct football and a proud club under absent ownership.',
  'Tansley Rovers': 'Steady, direct football under patient local ownership.',
  'Kirkby End FC': 'Counter-attacking survivors under an absent owner.',
  'Woolbridge Town': 'Ambitiously passing from the back under a local owner.',
  'Felbeck Wanderers': 'Direct and battling on under an absent owner.',
  'Stainby FC': 'Community-owned and pressing hard, roared on by the loudest support around.',
};

export function initials(name) {
  return (name || '')
    .replace(/\bFC\b/gi, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function hexToRgb(hex) {
  const h = (hex || '').trim().replace('#', '');
  const s = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (s.length !== 6 || /[^0-9a-f]/i.test(s)) return null;
  const n = parseInt(s, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0; let g = 0; let b = 0;
  if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; } else if (h < 180) { g = c; b = x; } else if (h < 240) { g = x; b = c; } else if (h < 300) { r = x; b = c; } else { r = c; b = x; }
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
}

function clubColour(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const hsl = rgbToHsl(rgb);
  if (hsl.l < 0.55) hsl.l = 0.55;
  return hslToRgb(hsl);
}

export function clubVars(hex) {
  const cc = clubColour(hex);
  return {
    border: cc ? `rgb(${cc.r}, ${cc.g}, ${cc.b})` : 'var(--line-strong)',
    glow: cc ? `rgba(${cc.r}, ${cc.g}, ${cc.b}, 0.5)` : 'rgba(200,241,53,0.22)',
    tint: cc ? `rgba(${cc.r}, ${cc.g}, ${cc.b}, 0.16)` : 'rgba(200,241,53,0.10)',
  };
}

export function asHonours(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed : [v];
    } catch (err) {
      return [v];
    }
  }
  return [];
}
